import { FC, useMemo, useState } from 'react';

import { Head, Link, router } from '@inertiajs/react';
import { ArrowRight, Sparkles } from 'lucide-react';

import { InfiniteScrollTrigger } from '../../components/shared/InfiniteScrollTrigger';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Routes } from '../../models/routes';
import {
    ChangelogCategory,
    ChangelogEntry,
} from '../../types/changelog-entry.types';
import { formatDate } from '../../utils';

type ChangelogIndexProps = {
    changelogEntries: ChangelogEntry[];
    currentCategory: ChangelogCategory | null;
    nextPage: number | null;
};

const CATEGORY_OPTIONS: ReadonlyArray<{
    value: ChangelogCategory | null;
    label: string;
}> = [
    { value: null, label: 'Усі' },
    { value: 'platform', label: 'Платформа' },
    { value: 'catalog', label: 'Каталог' },
    { value: 'fix', label: 'Виправлення' },
    { value: 'announcement', label: 'Оголошення' },
];

const CATEGORY_PRESENTATION = {
    platform: {
        label: 'Платформа',
        badgeClassName:
            'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-300',
    },
    catalog: {
        label: 'Каталог',
        badgeClassName:
            'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300',
    },
    fix: {
        label: 'Виправлення',
        badgeClassName:
            'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
    },
    announcement: {
        label: 'Оголошення',
        badgeClassName:
            'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
    },
} as const;

const categoryHref = (category: ChangelogCategory | null) =>
    category ? `${Routes.Changelog}?category=${category}` : Routes.Changelog;

const monthLabel = (date: string) =>
    new Intl.DateTimeFormat('uk-UA', {
        month: 'long',
        year: 'numeric',
    }).format(new Date(date));

const EntryAction: FC<{ entry: ChangelogEntry }> = ({ entry }) => {
    if (!entry.cta_url || !entry.cta_label) return null;

    const className =
        'mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline';
    const content = (
        <>
            {entry.cta_label}
            <ArrowRight className="h-4 w-4" />
        </>
    );

    return entry.cta_url.startsWith('/') ? (
        <Link href={entry.cta_url} className={className}>
            {content}
        </Link>
    ) : (
        <a
            href={entry.cta_url}
            className={className}
            target="_blank"
            rel="noopener noreferrer"
        >
            {content}
        </a>
    );
};

const ChangelogCard: FC<{ entry: ChangelogEntry }> = ({ entry }) => {
    const presentation = CATEGORY_PRESENTATION[entry.category];

    return (
        <article className="rounded-2xl border bg-card p-5 shadow-sm sm:p-6">
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge
                    variant="outline"
                    className={presentation.badgeClassName}
                >
                    {presentation.label}
                </Badge>
                {entry.universe_name && (
                    <Badge variant="neutral">{entry.universe_name}</Badge>
                )}
                <time
                    dateTime={entry.published_at}
                    className="text-xs text-muted-foreground sm:ml-auto"
                >
                    {formatDate(entry.published_at)}
                </time>
            </div>

            <h2 className="text-lg font-semibold sm:text-xl">{entry.title}</h2>
            <p className="mt-2 text-sm leading-6 whitespace-pre-line text-muted-foreground sm:text-base">
                {entry.description}
            </p>

            <EntryAction entry={entry} />
        </article>
    );
};

const ChangelogIndex: FC<ChangelogIndexProps> = ({
    changelogEntries,
    currentCategory,
    nextPage: initialNextPage,
}) => {
    const [entries, setEntries] = useState(changelogEntries);
    const [nextPage, setNextPage] = useState(initialNextPage);
    const [loading, setLoading] = useState(false);

    const groupedEntries = useMemo(() => {
        const groups: Array<{ label: string; entries: ChangelogEntry[] }> = [];

        entries.forEach((entry) => {
            const label = monthLabel(entry.published_at);
            const lastGroup = groups.at(-1);

            if (lastGroup?.label === label) {
                lastGroup.entries.push(entry);
            } else {
                groups.push({ label, entries: [entry] });
            }
        });

        return groups;
    }, [entries]);

    const loadMore = () => {
        if (!nextPage || loading) return;

        router.get(
            Routes.Changelog,
            {
                page: nextPage,
                ...(currentCategory ? { category: currentCategory } : {}),
            },
            {
                preserveScroll: true,
                preserveState: true,
                preserveUrl: true,
                only: ['changelogEntries', 'nextPage'],
                onStart: () => setLoading(true),
                onSuccess: (page) => {
                    const props = page.props as unknown as ChangelogIndexProps;
                    setEntries((current) => [
                        ...current,
                        ...props.changelogEntries,
                    ]);
                    setNextPage(props.nextPage);
                },
                onFinish: () => setLoading(false),
            },
        );
    };

    return (
        <>
            <Head title="Оновлення CartOn — нові функції та сети карток" />

            <div className="container mx-auto max-w-5xl px-4 py-10 sm:py-14">
                <header className="mx-auto max-w-3xl text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <Sparkles className="h-6 w-6" />
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
                        Оновлення CartOn
                    </h1>
                    <p className="mt-4 text-base leading-7 text-muted-foreground sm:text-lg">
                        Нові можливості платформи, важливі виправлення та
                        поповнення каталогів карток — в одному місці.
                    </p>
                </header>

                <nav
                    aria-label="Фільтр оновлень"
                    className="my-8 flex flex-wrap justify-center gap-2 sm:my-10"
                >
                    {CATEGORY_OPTIONS.map((option) => (
                        <Button
                            key={option.value ?? 'all'}
                            asChild
                            size="sm"
                            variant={
                                currentCategory === option.value
                                    ? 'default'
                                    : 'outline'
                            }
                        >
                            <Link href={categoryHref(option.value)}>
                                {option.label}
                            </Link>
                        </Button>
                    ))}
                </nav>

                {groupedEntries.length > 0 ? (
                    <div className="space-y-10">
                        {groupedEntries.map((group) => (
                            <section key={group.label}>
                                <h2 className="mb-5 text-sm font-semibold tracking-wider text-muted-foreground uppercase">
                                    {group.label}
                                </h2>
                                <div className="space-y-8">
                                    {group.entries.map((entry) => (
                                        <ChangelogCard
                                            key={entry.id}
                                            entry={entry}
                                        />
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                ) : (
                    <div className="rounded-2xl border border-dashed py-16 text-center">
                        <Sparkles className="mx-auto h-8 w-8 text-muted-foreground" />
                        <h2 className="mt-4 text-lg font-semibold">
                            Тут скоро з’являться оновлення
                        </h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Ми опублікуємо їх, щойно буде чим поділитися.
                        </p>
                    </div>
                )}

                {nextPage && (
                    <InfiniteScrollTrigger
                        className="mt-10"
                        loading={loading}
                        onLoadMore={loadMore}
                    />
                )}
            </div>
        </>
    );
};

export default ChangelogIndex;
